import { useMsal } from '@azure/msal-react'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Checkbox, Modal, Stack, TextInput } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'
import { hostUploadAd } from '../../data/advertisement.service'
import { DeviceAdvertisements } from '../../models/DeviceAdvertisements'
import { FileWithUrl } from '../../models/FileWithUrl'
import {
  convertToEndOfDay,
  setToMidnight,
  toLocalDateTimeString,
} from '../../utils/dateUtils'
import { AdUpload } from './AdUpload'

interface UploadAdModalProps {
  opened: boolean
  onClose: () => void
  deviceId: number
  deviceName: string
  organizationId: number
  onAdUploaded?: (newAdvertisement: DeviceAdvertisements) => void
}

export const UploadAdModal = ({
  opened,
  onClose,
  deviceId,
  deviceName,
  organizationId,
  onAdUploaded,
}: UploadAdModalProps) => {
  const [adFile, setAdFile] = useState<FileWithUrl | null>(null)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [isUploading, setIsUploading] = useState(false)
  const [useCustomDates, setUseCustomDates] = useState(false)
  const [adName, setAdName] = useState('')
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { instance } = useMsal()

  const handleUploadAd = async () => {
    if (!adFile) {
      notifications.show({
        title: 'Error',
        message: 'Please upload an ad',
        color: 'red',
      })
      return
    }

    if (useCustomDates && (!dateRange[0] || !dateRange[1])) {
      notifications.show({
        title: 'Error',
        message: 'Please select dates for your ad',
        color: 'red',
      })
      return
    }

    setIsUploading(true)
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const startDateLocal = useCustomDates
        ? toLocalDateTimeString(setToMidnight(dateRange[0]!))
        : null
      const endDateLocal = useCustomDates
        ? toLocalDateTimeString(convertToEndOfDay(dateRange[1]!))
        : null

      const response = await hostUploadAd(
        {
          deviceId,
          organizationId,
          name: adName || undefined,
          imageUrl: adFile.url,
          startDateLocal,
          endDateLocal,
          timezone,
        },
        instance,
      )

      notifications.show({
        title: 'Success',
        message: 'Ad uploaded successfully',
        color: 'green',
      })
      onClose()
      if (onAdUploaded) {
        onAdUploaded(response)
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload ad',
        color: 'red',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Upload Advertisement - ${deviceName}`}
      centered
      size='lg'
      radius='lg'
    >
      <Stack w='100%'>
        <AdUpload onFileUploadComplete={setAdFile} adFile={adFile} />

        <TextInput
          label='Ad Name (Optional)'
          placeholder='Summer Blowout Sale'
          value={adName}
          onChange={(e) => setAdName(e.target.value)}
          radius='md'
        />

        <Checkbox
          label='Select specific dates for this advertisement to be shown'
          checked={useCustomDates}
          onChange={(event) => setUseCustomDates(event.currentTarget.checked)}
        />

        {useCustomDates && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              padding: '20px 0',
            }}
          >
            <DatePicker
              type='range'
              value={dateRange}
              onChange={setDateRange}
              minDate={new Date()}
              maxLevel='year'
              weekendDays={[]}
              allowSingleDateInRange
              size={isMobile ? 'md' : 'lg'}
              display={'flex'}
            />
          </Box>
        )}

        <Button
          onClick={handleUploadAd}
          loading={isUploading}
          disabled={
            !adFile || (useCustomDates && (!dateRange[0] || !dateRange[1]))
          }
          fullWidth
          leftSection={<FontAwesomeIcon icon={faSave} />}
        >
          {useCustomDates &&
          dateRange[0] &&
          dateRange[1] &&
          dateRange[0] > new Date()
            ? 'Schedule Ad To Run'
            : 'Start Running Ad'}
        </Button>
      </Stack>
    </Modal>
  )
}
